import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { USD, fullDate, magnitude } from '../lib/format';
import { C, ink } from '../lib/theme';
import type { RecoveredChurn, RecoveredContraction, RecoveredInvoice } from '../lib/types';
import { ScaleModal, type Origin } from './ScaleModal';
import { RecoveredWeeklyChart } from './charts/RecoveredWeeklyChart';
import { IconAlert, IconExternal, IconTrendDown, IconTrendUp, IconX } from './icons';
import { EmptyState, ErrorBanner, Skeleton } from './primitives';

type Category = 'vencido' | 'falha' | 'contraction';
const ALL_CATEGORIES: Category[] = ['vencido', 'falha', 'contraction'];
type Channel = 'direto' | 'parceiro';
const ALL_CHANNELS: Channel[] = ['direto', 'parceiro'];

function InvoiceRow({ r }: { r: RecoveredInvoice }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 13,
      background: ink(0.04), border: `1px solid ${ink(0.09)}`,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: C.green, flex: 'none',
        boxShadow: `0 0 8px ${C.green}99`,
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 14, fontWeight: 750, color: C.fg }}>{r.nome || r.master_id || 'Cliente sem nome'}</span>
          {r.canal === 'parceiro' && (
            <span style={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.accent,
              background: 'rgba(255,107,71,0.14)', border: `1px solid rgba(255,107,71,0.3)`, borderRadius: 99, padding: '1px 6px',
            }}>Parceiro</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: ink(0.46), marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          falha "{r.motivo_falha}" · pago {fullDate(r.data_pagamento)}
        </div>
      </div>
      <div style={{
        fontSize: 13.5, fontWeight: 750, color: C.green, fontVariantNumeric: 'tabular-nums', flex: 'none', minWidth: 66, textAlign: 'right',
      }}>{USD(Number(r.valor_pago_centavos ?? 0) / 100)}</div>
      {r.hosted_invoice_url && (
        <button
          type="button" onClick={() => window.open(r.hosted_invoice_url!, '_blank', 'noopener,noreferrer')}
          title="Abrir a fatura no Stripe"
          style={{
            width: 29, height: 29, borderRadius: 9, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(74,125,255,0.4)', background: 'rgba(74,125,255,0.15)', color: C.blueSoft,
            cursor: 'pointer', font: 'inherit',
          }}
        ><IconExternal size={13} /></button>
      )}
    </div>
  );
}

/** Linha de churn OU contraction recuperado — mesmo formato de dado (`RecoveredChurn`/`RecoveredContraction` são o mesmo tipo), só muda o texto/verbo. */
function EventRow({ r, kind }: { r: RecoveredChurn | RecoveredContraction; kind: 'vencido' | 'contraction' }) {
  const stripeUrl = r.nova_sub_id ? `https://dashboard.stripe.com/subscriptions/${r.nova_sub_id}` : null;
  const dias = Math.round((new Date(r.nova_sub_criada).getTime() - new Date(r.event_date).getTime()) / 86_400_000);
  const verbo = kind === 'vencido' ? 'churn' : 'contraction';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 13,
      background: ink(0.04), border: `1px solid ${ink(0.09)}`,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: C.green, flex: 'none',
        boxShadow: `0 0 8px ${C.green}99`,
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 750, color: C.fg }}>{r.nome || r.master_id}</span>
          <span style={{ fontSize: 11.5, color: ink(0.42) }}>{r.product_category}</span>
        </div>
        <div style={{ fontSize: 12, color: ink(0.46), marginTop: 2 }}>
          {verbo} {fullDate(r.event_date)} · reassinou {fullDate(r.nova_sub_criada)} ({dias} dia{dias === 1 ? '' : 's'} depois)
        </div>
      </div>
      <div style={{
        fontSize: 13.5, fontWeight: 750, color: C.green, fontVariantNumeric: 'tabular-nums', flex: 'none', minWidth: 66, textAlign: 'right',
      }}>{USD(magnitude(r.mrr_lost))}</div>
      {stripeUrl && (
        <button
          type="button" onClick={() => window.open(stripeUrl, '_blank', 'noopener,noreferrer')}
          title="Abrir a nova assinatura no Stripe"
          style={{
            width: 29, height: 29, borderRadius: 9, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(74,125,255,0.4)', background: 'rgba(74,125,255,0.15)', color: C.blueSoft,
            cursor: 'pointer', font: 'inherit',
          }}
        ><IconExternal size={13} /></button>
      )}
    </div>
  );
}

const CATEGORY_META: Record<Category, { label: string; color: string; icon: (size: number) => React.ReactNode }> = {
  vencido: { label: 'Vencido (churn)', color: C.churn, icon: (s) => <IconX size={s} /> },
  falha: { label: 'Falha de pagamento', color: C.blueSoft, icon: (s) => <IconAlert size={s} /> },
  contraction: { label: 'Contraction', color: C.amber, icon: (s) => <IconTrendDown size={s} /> },
};

/** Tile clicável — mesmo papel dos buckets do `ConfirmadosPanel`, mas em card próprio (aqui não há um gráfico único que já sirva de filtro). */
function FilterTile({ cat, count, valor, ratePct, active, onToggle }: {
  cat: Category; count: number; valor: number; ratePct?: number; active: boolean; onToggle: () => void;
}) {
  const meta = CATEGORY_META[cat];
  return (
    <button
      type="button" onClick={onToggle}
      style={{
        textAlign: 'left', cursor: 'pointer', font: 'inherit', flex: 1, minWidth: 0,
        padding: '14px 16px', borderRadius: 15,
        background: active ? `${meta.color}18` : ink(0.035),
        border: `1.5px solid ${active ? `${meta.color}55` : ink(0.09)}`,
        opacity: active ? 1 : 0.55,
        transition: 'background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: active ? meta.color : ink(0.4), marginBottom: 8 }}>
        {meta.icon(14)}
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{meta.label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: C.fg, fontVariantNumeric: 'tabular-nums' }}>
        {USD(valor)}
      </div>
      <div style={{ fontSize: 12, color: ink(0.46), marginTop: 4 }}>
        {count} recuperado{count === 1 ? '' : 's'}{ratePct !== undefined ? ` · ${ratePct.toFixed(1)}% do total` : ''}
      </div>
    </button>
  );
}

/**
 * Par de botões quadrados Direto/Parceiro, encaixados na diagonal — mesmo
 * padrão do `TypeDiagonalToggle` de `ConfirmadosPanel` (pedido explícito de
 * reusar essa mecânica aqui). Só afeta a lista/tile de "falha" — churn e
 * contraction recuperados são sempre 'direto' por construção (a Nekt nunca
 * manda evento de parceiro pra `revops_lost_events`), então o toggle nem
 * aparece se não houver fatura de parceiro nenhuma no período.
 */
function ChannelDiagonalToggle({ active, onToggle }: { active: Set<Channel>; onToggle: (c: Channel) => void }) {
  const base = {
    position: 'absolute' as const, width: 34, height: 34, borderRadius: 11,
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', font: 'inherit',
    fontSize: 12, fontWeight: 800,
    transition: 'background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease, transform 0.15s ease',
  };
  const diretoOn = active.has('direto');
  const parceiroOn = active.has('parceiro');
  return (
    <div style={{ position: 'relative', width: 56, height: 56, flex: 'none' }}>
      <button
        type="button" onClick={() => onToggle('parceiro')} title="Filtrar Parceiro"
        style={{
          ...base, top: 22, left: 22, zIndex: parceiroOn ? 2 : 1,
          border: `1.5px solid ${parceiroOn ? C.accent : ink(0.14)}`,
          background: parceiroOn ? 'rgba(255,107,71,0.18)' : ink(0.04),
          color: parceiroOn ? C.accent : ink(0.32),
          opacity: parceiroOn ? 1 : 0.7,
          boxShadow: parceiroOn ? '0 0 14px -4px rgba(255,107,71,0.6)' : 'none',
        }}
      >P</button>
      <button
        type="button" onClick={() => onToggle('direto')} title="Filtrar Direto"
        style={{
          ...base, top: 0, left: 0, zIndex: diretoOn ? 2 : 1,
          border: `1.5px solid ${diretoOn ? C.blue : ink(0.14)}`,
          background: diretoOn ? 'rgba(74,125,255,0.18)' : ink(0.04),
          color: diretoOn ? C.blue : ink(0.32),
          opacity: diretoOn ? 1 : 0.7,
          boxShadow: diretoOn ? '0 0 14px -4px rgba(74,125,255,0.6)' : 'none',
        }}
      >D</button>
    </div>
  );
}

/**
 * Zona de Recuperados (2026-09-14, ampliada no mesmo dia) — receita que
 * voltou no período, 3 categorias: (1) "falha" — fatura que teve falha de
 * cobrança registrada e hoje está paga (`status_derivado='pago' AND
 * motivo_falha IS NOT NULL`); (2) "vencido" — churn cujo cliente abriu
 * assinatura ACTIVE nova depois do evento (cliente saiu e voltou); (3)
 * "contraction" — mesmo sinal que (2), mas pra evento de contraction (cliente
 * não saiu, só reduziu, e o valor voltou a subir). Os 3 tiles do topo são
 * filtro (mesmo padrão de `ConfirmadosPanel`): clique liga/desliga a
 * categoria na lista abaixo E no gráfico semanal.
 */
export function RecoveredModal({ open, origin, onClose, period }: {
  open: boolean;
  origin: Origin | null;
  onClose: () => void;
  period?: { from?: string; to?: string; channel?: string };
}) {
  const { data, loading, error, reload } = useAsync(
    () => (open ? api.recovered(period) : Promise.resolve(null)),
    [open, period?.from, period?.to, period?.channel],
  );
  const [active, setActive] = useState<Set<Category>>(new Set(ALL_CATEGORIES));
  const toggle = (c: Category) => setActive((prev) => {
    const next = new Set(prev);
    if (next.has(c)) next.delete(c); else next.add(c);
    return next;
  });
  const [activeChannels, setActiveChannels] = useState<Set<Channel>>(new Set(ALL_CHANNELS));
  const toggleChannel = (c: Channel) => setActiveChannels((prev) => {
    const next = new Set(prev);
    if (next.has(c)) next.delete(c); else next.add(c);
    return next;
  });

  const invoices = data?.invoices ?? [];
  const churns = data?.churns ?? [];
  const contractions = data?.contractions ?? [];
  const weekly = data?.weekly ?? [];
  const hasParceiro = invoices.some((r) => r.canal === 'parceiro');

  // Canal só existe pra fatura — churn/contraction recuperados são sempre
  // 'direto' por construção, então nunca dependem de `activeChannels`.
  const channelInvoices = useMemo(() => invoices.filter((r) => activeChannels.has(r.canal)), [invoices, activeChannels]);
  const visibleInvoices = active.has('falha') ? channelInvoices : [];
  const visibleChurns = active.has('vencido') ? churns : [];
  const visibleContractions = active.has('contraction') ? contractions : [];
  const noFilterSelected = active.size === 0 || activeChannels.size === 0;
  const isEmpty = !loading && !error && invoices.length === 0 && churns.length === 0 && contractions.length === 0;

  const falhaValor = useMemo(() => channelInvoices.reduce((s, r) => s + Number(r.valor_pago_centavos ?? 0) / 100, 0), [channelInvoices]);

  // Semana por categoria: vencido/contraction vêm prontos do backend (sempre
  // direto); falha precisa ser recalculada aqui porque o filtro de canal só
  // existe no cliente. Mesmo agrupamento por segunda-feira do backend.
  const weeklyWithChannel = useMemo(() => {
    const falhaByWeek = new Map<string, { valor: number; count: number }>();
    for (const r of channelInvoices) {
      const d = new Date(r.data_pagamento);
      const day = d.getUTCDay();
      const diffToMonday = day === 0 ? 6 : day - 1;
      const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - diffToMonday));
      const week = monday.toISOString().slice(0, 10);
      const slot = falhaByWeek.get(week) ?? { valor: 0, count: 0 };
      slot.valor += Number(r.valor_pago_centavos ?? 0) / 100;
      slot.count += 1;
      falhaByWeek.set(week, slot);
    }
    return weekly.map((w) => ({
      ...w,
      falha_valor: falhaByWeek.get(w.week_start)?.valor ?? 0,
      falha_count: falhaByWeek.get(w.week_start)?.count ?? 0,
    }));
  }, [weekly, channelInvoices]);

  const visibleTotal = useMemo(() => {
    const churnVal = visibleChurns.reduce((s, r) => s + magnitude(r.mrr_lost), 0);
    const contrVal = visibleContractions.reduce((s, r) => s + magnitude(r.mrr_lost), 0);
    return (active.has('falha') ? falhaValor : 0) + churnVal + contrVal;
  }, [falhaValor, active, visibleChurns, visibleContractions]);

  return (
    <ScaleModal
      open={open} origin={origin} onClose={onClose}
      title="Recuperados no período"
      subtitle="Faturas vencidas pagas, churns e contractions que reassinaram"
      width={900}
    >
      {loading && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={62} radius={13} />)}</div>}
      {error && <ErrorBanner message={error} onRetry={reload} />}

      {isEmpty && (
        <EmptyState
          tone="neutral" icon={<IconTrendUp size={22} />} title="Nada recuperado no período"
          text="Nenhuma fatura vencida foi paga e nenhum churn/contraction reassinou no filtro atual."
        />
      )}

      {!loading && !error && !isEmpty && data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
            borderRadius: 13, background: 'rgba(46,204,113,0.1)', border: `1px solid rgba(46,204,113,0.28)`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: ink(0.6) }}>
              Total recuperado no período{!noFilterSelected && (active.size < 3 || activeChannels.size < 2) ? ' (filtrado)' : ''}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.green, fontVariantNumeric: 'tabular-nums' }}>
              {USD(noFilterSelected ? 0 : visibleTotal)}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flex: 1 }}>
              <FilterTile
                cat="vencido" count={churns.length} valor={magnitude(data.kpis.churns_valor)}
                ratePct={magnitude(data.kpis.churn_recovery_rate_pct)} active={active.has('vencido')} onToggle={() => toggle('vencido')}
              />
              <FilterTile
                cat="falha" count={channelInvoices.length} valor={falhaValor}
                ratePct={magnitude(data.kpis.invoice_recovery_rate_pct)} active={active.has('falha')} onToggle={() => toggle('falha')}
              />
              <FilterTile
                cat="contraction" count={contractions.length} valor={magnitude(data.kpis.contractions_valor)}
                ratePct={magnitude(data.kpis.contraction_recovery_rate_pct)} active={active.has('contraction')} onToggle={() => toggle('contraction')}
              />
            </div>
            {hasParceiro && (
              <div style={{ flex: 'none', textAlign: 'center' }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: ink(0.4), marginBottom: 6 }}>
                  Canal (falha)
                </div>
                <ChannelDiagonalToggle active={activeChannels} onToggle={toggleChannel} />
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: ink(0.4), marginBottom: 9 }}>
              Comparativo semanal
            </div>
            <div style={{ height: 220, borderRadius: 15, background: ink(0.03), border: `1px solid ${ink(0.08)}`, padding: '14px 10px 6px' }}>
              <RecoveredWeeklyChart rows={weeklyWithChannel} active={active} />
            </div>
          </div>

          {noFilterSelected && (
            <EmptyState
              tone="neutral" icon={<IconTrendUp size={22} />} title="Nenhum filtro selecionado"
              text="Marque pelo menos uma categoria (e um canal, se aplicável) pra ver a lista de recuperados."
            />
          )}

          {visibleInvoices.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.blueSoft, marginBottom: 9 }}>
                Faturas recuperadas (falha) · {visibleInvoices.length}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {visibleInvoices.map((r) => <InvoiceRow key={r.invoice_id} r={r} />)}
              </div>
            </div>
          )}

          {visibleChurns.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.churn, marginBottom: 9 }}>
                Churns recuperados (vencido) · {visibleChurns.length}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {visibleChurns.map((r) => <EventRow key={r.event_id} r={r} kind="vencido" />)}
              </div>
            </div>
          )}

          {visibleContractions.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.amber, marginBottom: 9 }}>
                Contractions recuperadas · {visibleContractions.length}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {visibleContractions.map((r) => <EventRow key={r.event_id} r={r} kind="contraction" />)}
              </div>
            </div>
          )}
        </div>
      )}
    </ScaleModal>
  );
}
